import { getBase64Auth } from './network'
import { isNil } from './helpers'
import { GetServerSidePropsContext } from 'next'
import JSCookie from 'js-cookie'
import { IncomingHttpHeaders } from 'http'
import { isValidTraceId, makeUniqueId, spanIdHeaderKey, traceIdHeaderKey } from '@/logging/traceidUtils'

export interface Cookie {
  uid: string
  sid: string
  username: string
  hubspotutk: string
  redirectTo: string

  // development only
  dev_slot_toggle?: string
  session?: string
  'simscale-internal'?: string
  OauthAccessToken?: string
  OauthExpires?: string
  OauthEmail?: string
  developer?: string
  'trace-id'?: string
  'span-id'?: string
}

export const getCookies = (): Cookie => {
  if (typeof window === 'undefined') {
    return
  }
  const cookie = JSCookie.get()
  if (typeof cookie === 'undefined') {
    return
  }
  return {
    uid: cookie.uid,
    sid: cookie.sid,
    username: cookie.username,
    hubspotutk: cookie.hubspotutk,
    redirectTo: cookie.redirectTo,

    // development only
    dev_slot_toggle: cookie.dev_slot_toggle,
    session: cookie.session,
    'simscale-internal': cookie['simscale-internal'],
    OauthAccessToken: cookie.OauthAccessToken,
    OauthExpires: cookie.OauthExpires,
    OauthEmail: cookie.OauthEmail,
    developer: cookie.developer,
    'trace-id': cookie['trace-id'],
    'span-id': cookie['span-id']
  }
}

export const getCookie = (cookieName: string): string => {
  if (typeof window === 'undefined') {
    return
  }
  return JSCookie.get(cookieName)
}

export const setCookie = (name: string, value: string, domain?: string, expires?: string, path?: string): void => {
  if (expires === '') {
    var today = new Date().getTime()
    expires = new Date(today + 24 * 60 * 60 * 1000).toString()
  }
  if (isNil(domain)) {
    domain = ''
  }
  if (isNil(path)) {
    path = '/'
  }

  document.cookie = name + '=' + value + ';domain=' + domain + ';expires=' + expires + ';path=' + path
}

export const deleteCookie = (cookieName: string): void => {
  setCookie(cookieName, '', '', 'Thu, 01 Jan 1970 00:00:01 GMT', '/')
}

export const deleteAllCookies = (): void => {
  deleteCookie('username')
  deleteCookie('uid')
  deleteCookie('sid')
}

const getTraceIdFromHeadersOrCookies = (
  key: string,
  headers: IncomingHttpHeaders,
  cookies:
    | Partial<{
        [key: string]: string
      }>
    | Cookie
): string => {
  let id = ''
  if (!isNil(headers) && !isNil(headers[key])) {
    if (Array.isArray(headers[key])) {
      id = headers[key]?.toString()
    } else {
      id = headers[key] as string
    }
  }
  if (!isNil(cookies) && !isNil(cookies[key])) {
    id = cookies[key] as string
  }
  if (!isValidTraceId(id)) {
    id = makeUniqueId()
  }
  return id
}

export const createRequestHeaders = (
  req?: GetServerSidePropsContext['req']
): {
  'Content-type': string
  Authorization?: string
  Cookie?: string
  'span-id'?: string
  'trace-id'?: string
} => {
  const cookies = req?.cookies || getCookies()
  const authCookiesExist = !isNil(cookies) && !isNil(cookies.uid) && !isNil(cookies.sid)

  // if traceId and spanId are string[] then we need to convert them to string
  const spanId = getTraceIdFromHeadersOrCookies(spanIdHeaderKey, req?.headers, cookies)
  const traceId = getTraceIdFromHeadersOrCookies(traceIdHeaderKey, req?.headers, cookies)

  const headers: {
    'Content-type': string
    Authorization?: string
    Cookie?: string
    'span-id'?: string
    'trace-id'?: string
  } = {
    'Content-type': 'application/json; charset=UTF-8',
    Cookie: !isNil(cookies)
      ? Object.keys(cookies)
          .map((key) => {
            return key + '=' + cookies[key]
          })
          .join('; ')
      : '',
    'span-id': spanId,
    'trace-id': traceId
  }

  if (authCookiesExist) {
    headers.Authorization = 'Basic ' + getBase64Auth(cookies.uid, cookies.sid)
  }

  return headers
}

export const isLoggedIn = (): boolean => {
  if (typeof window === 'object') {
    // code is running in a browser environment
    return (
      document &&
      document.cookie &&
      document.cookie.search('uid') !== -1 &&
      document.cookie.search('sid') !== -1 &&
      document.cookie.search('username') !== -1
    )
  } else {
    // code is running in a non-browser environment
  }
}

export const isLoggedInSSR = (req: GetServerSidePropsContext['req']): boolean => {
  return req && req.cookies && req.cookies.uid && req.cookies.sid != null && req.cookies.username != null
}

export const isSimulationNewCookieSet = (): boolean => Boolean(document.cookie.match(/simulation\.new=([^;]*)/))

export const setSimulationNewCookie = (): void => {
  var today = new Date()
  var expires = new Date(today.getTime() + 10 * 60 * 1000) // 10 minutes
  setCookie('simulation.new', 'true', '', expires.toString(), '/')
}
